import { CheckCircle2, Download, Maximize2, Sparkles, X } from 'lucide-react';
import { useState } from 'react';
import { Button, Modal } from '@/components/ui';
import { downloadFile, extensionOf } from '@/lib/download';
import { CompareStage } from './CompareStage';
import type { CompareImages, CompareMode, ToggleSide } from './types';
import { useSliderPosition } from './useSliderPosition';
import { ViewModeSwitch } from './ViewModeSwitch';

const GUARANTEES = ['100% Geometric Replica', 'Scaffolding & Debris Erased', 'Exact Color & Finish Synthesis'];

/** Before/after viewer with split, side-by-side and toggle modes, inline or fullscreen. */
export function CompareViewer({ originalImage, renderImage }: CompareImages) {
  const [mode, setMode] = useState<CompareMode>('slider');
  const [side, setSide] = useState<ToggleSide>('render');
  const [fullscreen, setFullscreen] = useState(false);
  const slider = useSliderPosition();

  const download = () =>
    downloadFile(renderImage, `architectural-makeover-render-${Date.now()}.${extensionOf(renderImage)}`);
  const stage = { originalImage, renderImage, mode, slider, side, onSideChange: setSide };

  return (
    <div className="bg-[#1E293B] border border-slate-800 rounded-2xl p-5 shadow-2xl text-slate-100 space-y-4 relative overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30">
              <Sparkles className="w-4 h-4" />
            </span>
            <h2 className="text-base font-bold text-slate-100">Photorealistic Makeover Render</h2>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            100% geometric accuracy with scaffolding cleanup & paint synthesis
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <ViewModeSwitch value={mode} onChange={setMode} />
          <button
            type="button"
            onClick={() => setFullscreen(true)}
            className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-blue-300 border border-slate-700 font-semibold text-xs flex items-center gap-1.5 transition-all shadow-xs"
            title="Open comparison in full screen mode"
          >
            <Maximize2 className="w-3.5 h-3.5" /> Full Screen
          </button>
          <Button
            variant="success"
            size="md"
            className="rounded-lg font-semibold"
            onClick={download}
            id="download-render-btn"
          >
            <Download className="w-3.5 h-3.5" /> Export 8K Render
          </Button>
        </div>
      </div>

      <div className="bg-black/40 backdrop-blur-md px-4 py-2 rounded-lg border border-white/10 flex items-center justify-between text-[10px] font-mono text-slate-300">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="tracking-widest">RES: 7680 x 4320 (8K)</span>
        </div>
        <div className="flex items-center gap-3">
          <span>STRUCTURAL FIDELITY: 99.8%</span>
          <span className="text-white/20">|</span>
          <span>SAMPLES: 2048</span>
        </div>
      </div>

      <div className="relative rounded-xl overflow-hidden bg-slate-950 border border-slate-800 flex items-center justify-center select-none min-h-[380px] max-h-[550px]">
        <CompareStage {...stage} onExpand={() => setFullscreen(true)} />
      </div>

      <div className="bg-slate-950/80 rounded-xl p-3 border border-slate-800 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
        {GUARANTEES.map((text) => (
          <div key={text} className="flex items-center space-x-2 text-slate-300 font-medium">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <span>{text}</span>
          </div>
        ))}
      </div>

      <Modal
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        variant="fullscreen"
        labelledBy="compare-fullscreen-title"
      >
        <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-4 gap-4">
          <div className="flex items-center gap-3">
            <span className="p-2 rounded-xl bg-blue-600/20 text-blue-400 border border-blue-500/30">
              <Sparkles className="w-5 h-5" />
            </span>
            <div>
              <h3
                id="compare-fullscreen-title"
                className="text-base sm:text-lg font-bold text-white flex items-center gap-2"
              >
                Full-Screen Architectural Comparison
                <span className="text-xs font-mono font-normal text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded border border-emerald-800/80">
                  8K Render Mode
                </span>
              </h3>
              <p className="text-xs text-slate-400">
                Inspect high-resolution details, paint accuracy, and geometric fidelity. Press ESC or click Close to
                return.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <ViewModeSwitch value={mode} onChange={setMode} large />
            <Button variant="success" onClick={download} className="shadow-lg">
              <Download className="w-4 h-4" /> Export Render
            </Button>
            <button
              type="button"
              onClick={() => setFullscreen(false)}
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 transition-colors"
              title="Exit full-screen (Esc)"
              aria-label="Exit full screen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 w-full relative min-h-0 bg-slate-900/90 rounded-2xl border border-slate-800/80 overflow-hidden p-3 flex items-center justify-center">
          <CompareStage {...stage} fullscreen />
        </div>
      </Modal>
    </div>
  );
}
